import { useEffect, useState } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { getFilterValues } from "@/api/companies";
import { CFO_ACTION_LABELS } from "@/lib/cfoActions";
import { COMPANY_COLUMN_ALIASES } from "@/lib/companyColumnRenderers";
import type {
  AvailableColumn,
  PipelineStatus,
  ProjectMember,
} from "@/api/types";

/** One row of the advanced-filter builder. `id` is local-only — used
 *  as the React key while the row is in flight. Only (field, operator,
 *  value) is serialized into the listCompanies `filters` JSON. */
export interface FilterCondition {
  id: string;
  field: string;
  operator: string;
  value: unknown;
}

export type FilterValueType =
  | "text"
  | "number"
  | "select"
  | "boolean"
  | "date"
  | "tags"
  // Foreign-key column whose values are user UUIDs. Resolved against
  // the project's members for display (name) and selection.
  | "user"
  // Columns where the only meaningful query is "has a value or not"
  // (e.g. contact_phone, contact_email). No text/select operators.
  | "existence";

export interface FieldOption {
  value: string;
  label: string;
  type: FilterValueType;
}

/** Project-agnostic fields — always available regardless of project_type.
 *  Project-specific fields are merged in by `buildFieldOptions` from the
 *  available_columns catalog so CFO gets funding_stage / founder_origin,
 *  M&A gets revenue / ebitda / segment, etc. */
export const BASE_FIELD_OPTIONS: FieldOption[] = [
  { value: "name", label: "Company", type: "text" },
  { value: "score", label: "Score", type: "number" },
  { value: "pipeline_status", label: "Status", type: "select" },
  { value: "tags", label: "Tags", type: "tags" },
  // Foreign-key into the project's members — picker shows names,
  // condition value stores the UUID.
  { value: "owner_id", label: "Owner", type: "user" },
  { value: "is_client", label: "Is client", type: "boolean" },
  // Page-level tabs implicitly scope to is_archived=false. Surfacing
  // the field here lets an operator pull up archived rows by adding
  // `is_archived = true` to the builder — without needing a separate
  // Archived tab.
  { value: "is_archived", label: "Is archived", type: "boolean" },
  { value: "created_at", label: "Created", type: "date" },
  // Call Center: unanswered attempts in the current cycle. Backed by
  // `metadata.no_answer_count` server-side (the backend registers it as
  // an integer metadata field), which is also what the read-only
  // `no_answer_attempts` on CompanyRead projects — so filtering and the
  // call card agree on the same number.
  { value: "no_answer_count", label: "No-answer attempts", type: "number" },
  // PII fields with no value catalog — only "has phone" / "missing
  // phone"-style queries.
  { value: "contact_phone", label: "Contact phone", type: "existence" },
  { value: "contact_email", label: "Contact email", type: "existence" },
];

/** Legacy hardcoded list — kept exported so any caller that hasn't
 *  upgraded to `buildFieldOptions` yet still has a reasonable default.
 *  New code should prefer the dynamic builder. */
export const DEFAULT_FIELD_OPTIONS: FieldOption[] = BASE_FIELD_OPTIONS;

/** Map a backend column render type into a filter value type. The render
 *  catalog is wider than what we want for filters (currency renders €
 *  but filters like a number; `user` renders an avatar but filters via a
 *  catalog of IDs), so this is its own dispatch table. Unknown types
 *  fall back to "text" so a new backend column appears in the picker. */
function columnFilterType(colType: string): FilterValueType {
  switch (colType) {
    case "number":
    case "currency":
    case "currency_eur":
    case "percent":
      return "number";
    case "date":
      return "date";
    case "tags":
      return "tags";
    case "user":
      return "user";
    case "badge":
      return "select";
    default:
      return "text";
  }
}

/** Filter-side aliases. COMPANY_COLUMN_ALIASES maps display ids to
 *  the attribute the table column should *render* — that's wrong for
 *  filtering when the two diverge:
 *  - "score" (column id) renders `effective_score` (computed
 *    attribute on the row) but filters target the raw `score` DB
 *    column. The override below points the filter back to `score`.
 *  - `effective_score` itself isn't a filterable column at all —
 *    when it leaks in via available_columns or a stale saved view
 *    we collapse it onto `score`. */
const FILTER_FIELD_ALIASES: Record<string, string> = {
  score: "score",
  effective_score: "score",
};

/** Resolve a column id to the canonical filter target. Filter-side
 *  overrides win first, then the table-renderer aliases handle the
 *  common cases (owner → owner_id, status → pipeline_status, etc.). */
function canonicalKey(key: string): string {
  if (FILTER_FIELD_ALIASES[key]) return FILTER_FIELD_ALIASES[key];
  const aliases = COMPANY_COLUMN_ALIASES[key];
  return aliases?.[0] ?? key;
}

/** Merge the project-agnostic base list with any project-specific
 *  columns from `available_columns`. Metadata-backed columns are keyed
 *  by their `metadata_key` so the filter targets the JSONB path the
 *  backend expects. Display-id columns are normalized through
 *  COMPANY_COLUMN_ALIASES so an "owner" column collides with the
 *  BASE "owner_id" entry instead of duplicating it. */
export function buildFieldOptions(
  columns: AvailableColumn[],
): FieldOption[] {
  const baseValues = new Set(BASE_FIELD_OPTIONS.map((f) => f.value));
  const seen = new Set(baseValues);
  const extra: FieldOption[] = [];
  for (const col of columns) {
    const value =
      col.source === "metadata" && col.metadata_key
        ? col.metadata_key
        : canonicalKey(col.key);
    if (seen.has(value)) continue;
    seen.add(value);
    extra.push({
      value,
      label: col.label,
      type: columnFilterType(col.type),
    });
  }
  return [...BASE_FIELD_OPTIONS, ...extra];
}

export const OPERATORS_BY_TYPE: Record<
  FilterValueType,
  Array<{ value: string; label: string }>
> = {
  text: [
    { value: "eq", label: "is" },
    { value: "neq", label: "is not" },
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "does not contain" },
    { value: "in", label: "is one of" },
    { value: "not_in", label: "is not one of" },
    { value: "is_empty", label: "is empty" },
    { value: "is_not_empty", label: "is not empty" },
  ],
  number: [
    { value: "eq", label: "=" },
    { value: "neq", label: "≠" },
    { value: "gte", label: "≥" },
    { value: "lte", label: "≤" },
    { value: "gt", label: ">" },
    { value: "lt", label: "<" },
    { value: "between", label: "between" },
  ],
  select: [
    { value: "eq", label: "is" },
    { value: "neq", label: "is not" },
    { value: "in", label: "is one of" },
    { value: "not_in", label: "is not one of" },
  ],
  tags: [
    { value: "contains", label: "has" },
    { value: "not_contains", label: "does not have" },
    { value: "is_empty", label: "has none" },
    { value: "is_not_empty", label: "has any" },
  ],
  date: [
    { value: "gte", label: "after" },
    { value: "lte", label: "before" },
    { value: "between", label: "between" },
  ],
  boolean: [{ value: "eq", label: "is" }],
  user: [
    { value: "eq", label: "is" },
    { value: "neq", label: "is not" },
    { value: "is_empty", label: "unassigned" },
    { value: "is_not_empty", label: "assigned" },
  ],
  existence: [
    { value: "is_not_empty", label: "exists" },
    { value: "is_empty", label: "is missing" },
  ],
};

const NO_VALUE_OPERATORS = new Set(["is_empty", "is_not_empty"]);

// Operators that look for a substring rather than an exact match —
// these always render as a plain text input even when the field has
// an autocomplete catalog (a contains query isn't a value pick).
const SUBSTRING_OPERATORS = new Set(["contains", "not_contains"]);

interface SliderFieldConfig {
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
}

/** Numeric fields whose `between` editor renders as a dual-range
 *  slider instead of two number inputs. Fields not in this table
 *  still use the two-input fallback — slider only makes sense when
 *  the value space has known bounds. */
const SLIDER_FIELD_CONFIGS: Record<string, SliderFieldConfig> = {
  score: { min: 0, max: 100, step: 5, format: (v) => String(v) },
  headcount: { min: 0, max: 500, step: 5, format: (v) => String(v) },
  revenue_euros: {
    min: 0,
    max: 50_000_000,
    step: 100_000,
    format: formatEuroShort,
  },
  ebitda_euros: {
    min: 0,
    max: 10_000_000,
    step: 50_000,
    format: formatEuroShort,
  },
  net_result_euros: {
    min: 0,
    max: 5_000_000,
    step: 50_000,
    format: formatEuroShort,
  },
  ebitda_margin_percent: {
    min: 0,
    max: 50,
    step: 1,
    format: (v) => `${v}%`,
  },
  debt_leverage_ratio: {
    min: 0,
    max: 10,
    step: 0.5,
    format: (v) => `${v}x`,
  },
  finance_score: { min: 0, max: 50, step: 1, format: (v) => String(v) },
  governance_score: { min: 0, max: 45, step: 1, format: (v) => String(v) },
  manual_notation: { min: 0, max: 18, step: 1, format: (v) => String(v) },
};

function formatEuroShort(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return String(v);
}

function makeId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `cond-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** A fresh row defaults to the first available field and that field's
 *  first operator. */
export function emptyCondition(
  fields: FieldOption[] = DEFAULT_FIELD_OPTIONS,
): FilterCondition {
  const first = fields[0] ?? DEFAULT_FIELD_OPTIONS[0];
  return {
    id: makeId(),
    field: first.value,
    operator: OPERATORS_BY_TYPE[first.type][0]?.value ?? "eq",
    value: "",
  };
}

/** Strip the local-only `id` from a list of conditions — used when
 *  persisting into saved views or the listCompanies payload, so the
 *  random UUID doesn't leak into JSON the backend has to validate. */
export function stripFilterIds(
  conditions: FilterCondition[],
): Array<Omit<FilterCondition, "id">> {
  return conditions.map((c) => ({
    field: c.field,
    operator: c.operator,
    value: c.value,
  }));
}

/** Inverse of `stripFilterIds` — accepts whatever a saved view stores
 *  in its `advanced_filters` key, validates each entry shape, and
 *  re-attaches a fresh local id so React keys stay stable.
 *  Tolerant of unknown shapes (returns []) so a malformed view doesn't
 *  break the page. Stale fields (e.g. `effective_score` from views
 *  saved before the filter-alias fix) are rewritten via the same
 *  FILTER_FIELD_ALIASES map the field dropdown uses, so old views
 *  don't keep firing rejected filter conditions at the backend. */
export function deserializeFilters(raw: unknown): FilterCondition[] {
  if (!Array.isArray(raw)) return [];
  const out: FilterCondition[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    if (typeof o.field !== "string" || typeof o.operator !== "string") continue;
    out.push({
      id: makeId(),
      field: FILTER_FIELD_ALIASES[o.field] ?? o.field,
      operator: o.operator,
      value: o.value,
    });
  }
  return out;
}

/** Serialize conditions for the backend `filters` query param.
 *  Strips the local-only `id` field and any conditions that don't have
 *  a value yet (so a half-built row doesn't crash the API). */
export function serializeFilters(conditions: FilterCondition[]): string | undefined {
  const ready = conditions
    .filter((c) => c.field && c.operator)
    .filter((c) => {
      if (NO_VALUE_OPERATORS.has(c.operator)) return true;
      if (Array.isArray(c.value)) {
        return c.value.some((v) => v !== "" && v !== null && v !== undefined);
      }
      return c.value !== "" && c.value !== null && c.value !== undefined;
    })
    .map((c) => ({ field: c.field, operator: c.operator, value: c.value }));
  if (ready.length === 0) return undefined;
  return JSON.stringify({ conditions: ready });
}

// ─── Top-level component ─────────────────────────────────────────────────

export interface AdvancedFiltersProps {
  filters: FilterCondition[];
  onChange: (next: FilterCondition[]) => void;
  slug: string;
  /** Field options shown in the field selector. Defaults to the common
   *  set; pages can pass a project-aware list derived from the
   *  available_columns catalog. */
  fields?: FieldOption[];
  /** Project members — used by the `user` field type to render names
   *  in the picker (the underlying owner_id column stores UUIDs). */
  members?: ProjectMember[];
  /** Effective pipeline statuses — used to map the raw status keys
   *  returned by /filter-values/pipeline_status to their human labels
   *  in the picker. Pass the project's full status list (including
   *  won) so any value that appears in the data resolves correctly. */
  pipelineStatuses?: PipelineStatus[];
}

export function AdvancedFilters({
  filters,
  onChange,
  slug,
  fields = DEFAULT_FIELD_OPTIONS,
  members = [],
  pipelineStatuses = [],
}: AdvancedFiltersProps) {
  function fieldType(field: string): FilterValueType {
    return fields.find((f) => f.value === field)?.type ?? "text";
  }
  function add() {
    onChange([...filters, emptyCondition(fields)]);
  }
  function update(id: string, patch: Partial<FilterCondition>) {
    onChange(filters.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }
  function remove(id: string) {
    onChange(filters.filter((f) => f.id !== id));
  }

  return (
    <div className="space-y-2">
      {filters.map((cond, i) => (
        <div
          key={cond.id}
          className="flex flex-wrap items-center gap-2 rounded-xl bg-white px-3 py-2 shadow-card-rest"
        >
          <span
            className={cn(
              "w-8 shrink-0 text-[10px] font-bold uppercase tracking-wider",
              i === 0 ? "invisible" : "text-indigo-400",
            )}
          >
            and
          </span>

          <Select
            value={cond.field}
            onValueChange={(field) => {
              // Switching the field resets the operator + value so we
              // never end up with a (number, "contains", "abc")
              // triplet the API can't make sense of.
              const t = fieldType(field);
              const op = OPERATORS_BY_TYPE[t][0]?.value ?? "eq";
              update(cond.id, { field, operator: op, value: "" });
            }}
          >
            <SelectTrigger className="h-8 w-[150px] border-slate-200 bg-slate-50 text-xs font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fields.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <OperatorSelect
            type={fieldType(cond.field)}
            value={cond.operator}
            onChange={(operator) =>
              update(cond.id, {
                operator,
                // `between` needs an array; everything else expects a
                // scalar. Reset whenever the shape changes so we don't
                // feed an array to a numeric input.
                value: operator === "between" ? ["", ""] : "",
              })
            }
          />

          <FilterValueInput
            condition={cond}
            slug={slug}
            type={fieldType(cond.field)}
            members={members}
            pipelineStatuses={pipelineStatuses}
            onChange={(value) => update(cond.id, { value })}
          />

          <button
            type="button"
            onClick={() => remove(cond.id)}
            className="ml-auto rounded-md p-1.5 text-slate-400 transition-all hover:bg-rose-50 hover:text-rose-500"
            aria-label="Remove filter"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={add}
        className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
      >
        <Plus className="h-3.5 w-3.5" /> Add filter
      </button>
    </div>
  );
}

// ─── Operator select ─────────────────────────────────────────────────────

export function OperatorSelect({
  type,
  value,
  onChange,
}: {
  type: FilterValueType;
  value: string;
  onChange: (next: string) => void;
}) {
  const options = OPERATORS_BY_TYPE[type] ?? OPERATORS_BY_TYPE.text;
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-[120px] border-slate-200 bg-slate-50 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ─── Value input dispatcher ──────────────────────────────────────────────

export interface FilterValueInputProps {
  condition: FilterCondition;
  slug: string;
  type: FilterValueType;
  members: ProjectMember[];
  pipelineStatuses: PipelineStatus[];
  onChange: (value: unknown) => void;
}

export function FilterValueInput({
  condition,
  slug,
  type,
  members,
  pipelineStatuses,
  onChange,
}: FilterValueInputProps) {
  // Map status keys to their human labels. Empty when the field
  // isn't pipeline_status (or when statuses haven't loaded yet),
  // and the pickers fall back to displaying the raw value.
  const labelMap: Record<string, string> | undefined =
    condition.field === "pipeline_status" && pipelineStatuses.length > 0
      ? Object.fromEntries(pipelineStatuses.map((s) => [s.key, s.label]))
      : condition.field === "cfo_action"
        ? CFO_ACTION_LABELS
        : undefined;
  if (NO_VALUE_OPERATORS.has(condition.operator)) {
    return <span className="text-xs text-slate-300">(no value)</span>;
  }

  // Existence-type fields only ever use is_empty / is_not_empty.
  // The NO_VALUE_OPERATORS branch above already caught those, so if
  // we somehow reach here with another operator we render nothing
  // rather than a misleading input.
  if (type === "existence") {
    return null;
  }

  // Foreign-key into members: dedicated picker that resolves UUIDs
  // to names. eq / neq use a single-pick, is_*_empty had no value.
  if (type === "user") {
    return (
      <MemberSelect
        members={members}
        value={typeof condition.value === "string" ? condition.value : ""}
        onChange={(next) => onChange(next)}
      />
    );
  }

  // Tags: dedicated picker backed by /filter-values/tags. contains /
  // not_contains take a list of names; eq / neq use a single name.
  if (type === "tags") {
    const multi =
      condition.operator === "contains" || condition.operator === "not_contains";
    return (
      <TagSelect
        slug={slug}
        value={condition.value}
        multi={multi}
        onChange={(next) => onChange(next)}
      />
    );
  }

  // `between` owns its tuple input first — the operator wins over the
  // type's default single-input dispatch. Numeric fields with known
  // bounds get a dual-range slider; everything else falls back to two
  // typed inputs.
  if (condition.operator === "between") {
    const tuple = Array.isArray(condition.value)
      ? (condition.value as [unknown, unknown])
      : ["", ""];
    const isDate = type === "date";
    const sliderConfig =
      !isDate && type === "number"
        ? SLIDER_FIELD_CONFIGS[condition.field]
        : undefined;
    if (sliderConfig) {
      const lo = clampToConfig(asNumberOr(tuple[0], sliderConfig.min), sliderConfig);
      const hi = clampToConfig(asNumberOr(tuple[1], sliderConfig.max), sliderConfig);
      return (
        <BetweenSlider
          config={sliderConfig}
          value={[lo, hi]}
          onChange={(next) => onChange(next)}
        />
      );
    }
    return (
      <div className="flex items-center gap-1">
        <Input
          type={isDate ? "date" : "number"}
          value={asInputValue(tuple[0])}
          onChange={(e) => onChange([e.target.value, tuple[1]])}
          className="h-8 w-24 text-xs"
        />
        <span className="text-xs text-slate-400">—</span>
        <Input
          type={isDate ? "date" : "number"}
          value={asInputValue(tuple[1])}
          onChange={(e) => onChange([tuple[0], e.target.value])}
          className="h-8 w-24 text-xs"
        />
      </div>
    );
  }

  // Strict-typed inputs that never use the backend catalog — picking
  // a boolean from autocomplete or running `is` on a date wouldn't
  // make sense, so the typed editor wins regardless of operator.
  if (type === "boolean") {
    return (
      <Select
        value={String(!!condition.value)}
        onValueChange={(v) => onChange(v === "true")}
      >
        <SelectTrigger className="h-8 w-20 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">Yes</SelectItem>
          <SelectItem value="false">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (type === "number") {
    return (
      <Input
        type="number"
        value={asInputValue(condition.value)}
        onChange={(e) =>
          onChange(e.target.value === "" ? "" : Number(e.target.value))
        }
        className="h-8 w-28 text-xs"
      />
    );
  }

  if (type === "date") {
    return (
      <Input
        type="date"
        value={typeof condition.value === "string" ? condition.value : ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-36 text-xs"
      />
    );
  }

  // `contains` / `not_contains` are substring queries — the autocomplete
  // catalog isn't the right UX (no value-pick), so always free text.
  if (SUBSTRING_OPERATORS.has(condition.operator)) {
    return (
      <Input
        type="text"
        value={typeof condition.value === "string" ? condition.value : ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Value…"
        className="h-8 w-40 text-xs"
      />
    );
  }

  // Multi-value picker: in / not_in. Always tries the backend
  // autocomplete; the picker itself falls back to a free-text add
  // pattern when the catalog is empty (e.g. blocklisted PII field).
  if (condition.operator === "in" || condition.operator === "not_in") {
    // Backend `/filter-values/` returns a uniform string[] payload
    // even for numeric / boolean columns, so coerce every entry to a
    // string — comparisons like ["75"].includes(75) would silently
    // fail and the picker would re-offer an already-selected value.
    const stringValues = Array.isArray(condition.value)
      ? (condition.value as unknown[]).map((v) =>
          v === null || v === undefined ? "" : String(v),
        )
      : [];
    return (
      <MultiValueInput
        field={condition.field}
        slug={slug}
        value={stringValues}
        labelMap={labelMap}
        onChange={(next) => onChange(next)}
      />
    );
  }

  // Default single-value path (eq / neq / gt / lt / gte / lte for
  // text/select/tags — numeric typed inputs were handled above). The
  // picker decides at runtime whether to render a popover (catalog
  // returned values) or a plain text input (catalog empty / blocked).
  const single =
    condition.value === null ||
    condition.value === undefined ||
    typeof condition.value === "object"
      ? ""
      : String(condition.value);
  return (
    <SingleValueInput
      field={condition.field}
      slug={slug}
      value={single}
      labelMap={labelMap}
      onChange={(next) => onChange(next)}
    />
  );
}

function asNumberOr(v: unknown, fallback: number): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function clampToConfig(v: number, c: SliderFieldConfig): number {
  if (v < c.min) return c.min;
  if (v > c.max) return c.max;
  return v;
}

/** Dual-range slider used when a known numeric field gets the `between`
 *  operator. Drag-on-local-state / commit-to-parent so we don't fire a
 *  refetch on every pixel of the drag. */
function BetweenSlider({
  config,
  value,
  onChange,
}: {
  config: SliderFieldConfig;
  value: [number, number];
  onChange: (next: [number, number]) => void;
}) {
  const [local, setLocal] = useState<[number, number]>(value);
  useEffect(() => {
    setLocal(value);
  }, [value]);
  return (
    <div className="flex min-w-[220px] items-center gap-2">
      <span className="w-14 text-right font-mono text-[10px] text-slate-600">
        {config.format(local[0])}
      </span>
      <SliderPrimitive.Root
        min={config.min}
        max={config.max}
        step={config.step}
        value={local}
        onValueChange={(v) =>
          setLocal([v[0] ?? config.min, v[1] ?? config.max] as [number, number])
        }
        onValueCommit={(v) =>
          onChange([v[0] ?? config.min, v[1] ?? config.max] as [number, number])
        }
        className="relative flex h-5 w-full flex-1 select-none touch-none items-center"
      >
        <SliderPrimitive.Track className="relative h-1.5 grow rounded-full bg-slate-200">
          <SliderPrimitive.Range className="absolute h-full rounded-full bg-indigo-500" />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          aria-label="Minimum"
          className="block h-4 w-4 cursor-grab rounded-full border-2 border-indigo-500 bg-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1 active:cursor-grabbing"
        />
        <SliderPrimitive.Thumb
          aria-label="Maximum"
          className="block h-4 w-4 cursor-grab rounded-full border-2 border-indigo-500 bg-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1 active:cursor-grabbing"
        />
      </SliderPrimitive.Root>
      <span className="w-14 font-mono text-[10px] text-slate-600">
        {config.format(local[1])}
      </span>
    </div>
  );
}

function asInputValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "string") return v;
  return "";
}

// ─── Backend-backed catalog hook ─────────────────────────────────────────

interface CatalogOption {
  value: string;
  label: string;
}

/** Fetches `/filter-values/{field}` eagerly and on every debounced
 *  `search` change. `loaded` flips true once the first response (or
 *  error) comes back, so callers can distinguish "we haven't checked
 *  yet" from "we checked and got 0". */
function useFilterOptions(
  slug: string,
  field: string,
  search: string,
): { options: CatalogOption[]; loading: boolean; loaded: boolean } {
  const debounced = useDebounce(search, 200);
  const [values, setValues] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!slug || !field) {
      setValues([]);
      setLoaded(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getFilterValues(slug, field, debounced)
      .then((res) => {
        if (cancelled) return;
        setValues(res);
      })
      .catch(() => {
        if (cancelled) return;
        setValues([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, field, debounced]);

  return {
    options: values.map((v) => ({ value: v, label: v })),
    loading,
    loaded,
  };
}

// ─── Single-value picker (eq / neq on select/tags) ───────────────────────

interface SingleValueInputProps {
  field: string;
  slug: string;
  value: string;
  /** Optional value→label override. When provided, the picker shows
   *  the mapped label instead of the raw value (e.g. pipeline status
   *  keys → their configured labels). */
  labelMap?: Record<string, string>;
  onChange: (v: string) => void;
}

function SingleValueInput({
  field,
  slug,
  value,
  labelMap,
  onChange,
}: SingleValueInputProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  // Eager fetch — the catalog response decides whether this field
  // renders as a picker or as a plain text input. No hardcoded
  // allowlist; PII / blocklisted columns just return [].
  const { options: rawOptions, loading, loaded } = useFilterOptions(
    slug,
    field,
    search,
  );
  // Overlay the label map on the fetched values — labels drive what
  // the operator sees, raw values stay as the condition payload.
  const options = labelMap
    ? rawOptions.map((o) => ({
        value: o.value,
        label: labelMap[o.value] ?? o.label,
      }))
    : rawOptions;

  // First successful fetch came back with nothing — the field has no
  // catalog (free text, blocklisted, or PII). Fall back to a plain
  // input so the operator can still type a value.
  if (loaded && !loading && options.length === 0 && search.trim() === "") {
    return (
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Value…"
        className="h-8 w-40 text-xs"
      />
    );
  }

  // Resolve the stored value to its friendly label for the trigger
  // display — preferring the label map, then the catalog, then the
  // raw value so any orphaned value still shows something.
  const displayLabel =
    labelMap?.[value] ?? options.find((o) => o.value === value)?.label ?? value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-8 min-w-[140px] max-w-[220px] items-center justify-between rounded-md border border-slate-200 bg-white px-3 text-xs hover:bg-slate-50",
            !value && "text-slate-400",
          )}
        >
          <span className="truncate">{displayLabel || "Pick…"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-2" align="start">
        <Input
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-1.5 h-7 text-xs"
        />
        <div className="max-h-[200px] overflow-y-auto">
          {loading && options.length === 0 ? (
            <div className="px-2 py-1 text-xs text-slate-400">Loading…</div>
          ) : options.length === 0 ? (
            <div className="px-2 py-1 text-xs text-slate-400">No matches</div>
          ) : (
            options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                  setSearch("");
                }}
                className={cn(
                  "block w-full truncate rounded px-2 py-1.5 text-left text-xs",
                  o.value === value
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-slate-700 hover:bg-slate-100",
                )}
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Multi-value picker (in / not_in) ────────────────────────────────────

interface MultiValueInputProps {
  field: string;
  slug: string;
  value: string[];
  /** Optional value→label override; same role as in SingleValueInput. */
  labelMap?: Record<string, string>;
  onChange: (next: string[]) => void;
}

function MultiValueInput({
  field,
  slug,
  value,
  labelMap,
  onChange,
}: MultiValueInputProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [freeText, setFreeText] = useState("");
  // Eager fetch — `loaded` + `options.length` tell us whether the
  // popover should show a catalog or the free-text add pattern.
  const { options: rawOptions, loading, loaded } = useFilterOptions(
    slug,
    field,
    search,
  );
  // Overlay the label map on the fetched values.
  const options = labelMap
    ? rawOptions.map((o) => ({
        value: o.value,
        label: labelMap[o.value] ?? o.label,
      }))
    : rawOptions;
  // Treat the field as catalog-less once the first fetch comes back
  // with nothing AND no in-flight search-narrowed re-fetch is
  // running.
  const hasCatalog =
    !loaded || loading || options.length > 0 || search.trim() !== "";

  const filtered = options.filter((o) => !value.includes(o.value));
  const labelOf = (v: string) =>
    labelMap?.[v] ?? options.find((o) => o.value === v)?.label ?? v;

  function add(next: string) {
    const t = next.trim();
    if (!t || value.includes(t)) return;
    onChange([...value, t]);
    setSearch("");
    setFreeText("");
  }
  function remove(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {value.map((v, i) => (
        <span
          key={`${v}-${i}`}
          className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700"
        >
          {labelOf(v)}
          <button
            type="button"
            onClick={() => remove(i)}
            aria-label={`Remove ${labelOf(v)}`}
            className="text-indigo-400 hover:text-indigo-700"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </span>
      ))}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="rounded-full border border-dashed border-indigo-200 px-2 py-0.5 text-[10px] text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700"
          >
            + add
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-2" align="start">
          {hasCatalog ? (
            <>
              <Input
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-1.5 h-7 text-xs"
              />
              <div className="max-h-[200px] overflow-y-auto">
                {loading && filtered.length === 0 ? (
                  <div className="px-2 py-1 text-xs text-slate-400">
                    Loading…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="px-2 py-1 text-xs text-slate-400">
                    {search.trim() ? "No matches" : "All selected"}
                  </div>
                ) : (
                  filtered.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => add(o.value)}
                      className="block w-full truncate rounded px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-100"
                    >
                      {o.label}
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-1">
              <Input
                placeholder="Value…"
                type="text"
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    add(freeText);
                  }
                }}
                className="h-7 text-xs"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => add(freeText)}
                disabled={freeText.trim() === ""}
                className="h-7 px-2 text-xs"
              >
                Add
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ─── Member picker (owner_id / `user` type) ──────────────────────────────

interface MemberSelectProps {
  members: ProjectMember[];
  value: string;
  onChange: (id: string) => void;
}

/** Single-pick picker over the project's members. The advanced
 *  builder's `user` field type only accepts eq / neq (per OPERATORS_BY_
 *  TYPE.user), so multi-select isn't needed here — `in` / `not_in`
 *  aren't valid operators for this type. The condition stores the
 *  member's UUID; the trigger renders their full_name. */
function MemberSelect({ members, value, onChange }: MemberSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = members.find((m) => m.id === value);
  const q = search.trim().toLowerCase();
  const filtered = q
    ? members.filter(
        (m) =>
          m.full_name.toLowerCase().includes(q) ||
          (m.email ?? "").toLowerCase().includes(q),
      )
    : members;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-8 min-w-[160px] max-w-[220px] items-center justify-between rounded-md border border-slate-200 bg-white px-3 text-xs hover:bg-slate-50",
            !value && "text-slate-400",
          )}
        >
          <span className="truncate">
            {selected?.full_name || "Select…"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-2" align="start">
        {members.length > 8 && (
          <Input
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-1.5 h-7 text-xs"
          />
        )}
        <div className="max-h-[200px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-2 py-1 text-xs text-slate-400">No matches</div>
          ) : (
            filtered.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  onChange(m.id);
                  setOpen(false);
                  setSearch("");
                }}
                className={cn(
                  "block w-full truncate rounded px-2 py-1.5 text-left text-xs",
                  m.id === value
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-slate-700 hover:bg-slate-100",
                )}
              >
                {m.full_name}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Tag picker (`tags` type) ────────────────────────────────────────────

interface TagSelectProps {
  slug: string;
  value: unknown;
  multi: boolean;
  onChange: (next: unknown) => void;
}

/** Backed by `/filter-values/tags` so the catalog reflects whatever
 *  the project currently uses. `contains` / `not_contains` operate
 *  on a list of tag names; `eq` / `neq` (if exposed later) would use
 *  a single name. Stores raw names; the backend resolves them
 *  (auto-create on bulk-action). */
function TagSelect({ slug, value, multi, onChange }: TagSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { options, loading, loaded } = useFilterOptions(slug, "tags", search);
  const values: string[] = Array.isArray(value)
    ? (value as unknown[]).map((v) => String(v))
    : typeof value === "string" && value
      ? [value]
      : [];
  const filtered = options.filter((o) => !values.includes(o.value));
  return (
    <div className="flex flex-wrap items-center gap-1">
      {values.map((tag, i) => (
        <span
          key={`${tag}-${i}`}
          className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700"
        >
          {tag}
          <button
            type="button"
            onClick={() => {
              const next = values.filter((_, j) => j !== i);
              onChange(multi ? next : next[0] ?? "");
            }}
            aria-label={`Remove ${tag}`}
            className="text-amber-500 hover:text-amber-700"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </span>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="rounded-full border border-dashed border-amber-300 px-2 py-0.5 text-[10px] text-amber-600 hover:bg-amber-50"
          >
            + tag
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-2" align="start">
          <Input
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-1.5 h-7 text-xs"
          />
          <div className="max-h-[180px] overflow-y-auto">
            {loading && filtered.length === 0 ? (
              <div className="px-2 py-1 text-xs text-slate-400">Loading…</div>
            ) : !loaded ? null : filtered.length === 0 ? (
              <div className="px-2 py-1 text-xs text-slate-400">
                {search.trim() ? "No matches" : "No tags"}
              </div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => {
                    onChange(multi ? [...values, o.value] : o.value);
                    if (!multi) setOpen(false);
                    setSearch("");
                  }}
                  className="block w-full truncate rounded px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-100"
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
